import { useEffect, useMemo, useRef } from "react";
import { Avatar } from "../../../../../../components/Avatar/Avatar";
import AgentAssignmentDropdown from "../../../AgentAssignmentDropdown";
import EmailMessageShimmer from "./components/EmailMessageShimmer";
import EmailMessageBlock from "./components/EmailMessageBlock";
import InboxInput from "./components/InboxInput";
import type { InboxMail, InboxThread } from "../../utils/types";
import { getDateTimeByFormat } from "../../../../../../redux/slices/dateTimeFormatSlice";
import { Tooltip } from "../../../../../../components/Tooltip/Tooltip";

interface InboxConversationPanelProps {
  mails: InboxMail[];
  selectedThread: InboxThread | null;
  selectedThreadId: string;
  isLoading: boolean;
  shouldScrollToBottom?: boolean;
  setShouldScrollToBottom?: (value: boolean) => void;
  onEmailBuilderClick?: (startTab?: string) => void;
  onAssignAgents?: (agentIds: string[]) => Promise<void>;
}

const DateStamp = ({ date }: { date: string }) => (
  <div className="flex items-center px-[12px] py-[4px]">
    <div className="flex-1 h-px bg-border-gray dark:bg-border-gray-dk" />
    <span className="text-sm font-semibold text-verba-indigo dark:text-verba-indigo-dk px-[12px] py-[2px] rounded-full border border-border-gray dark:border-border-gray-dk">
      {date}
    </span>
    <div className="flex-1 h-px bg-border-gray dark:bg-border-gray-dk" />
  </div>
);

const InboxConversationPanel: React.FC<InboxConversationPanelProps> = ({
  mails,
  selectedThread,
  selectedThreadId,
  isLoading,
  shouldScrollToBottom,
  setShouldScrollToBottom,
  onEmailBuilderClick,
  onAssignAgents,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (shouldScrollToBottom && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      setShouldScrollToBottom?.(false);
    }
  }, [shouldScrollToBottom, setShouldScrollToBottom]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [selectedThreadId]);

  const mailsWithDateSeparators = useMemo(() => {
    const result: Array<
      { type: "date"; date: string } | { type: "message"; mail: InboxMail }
    > = [];
    let lastDateLabel = "";

    mails?.forEach((mail) => {
      const dateLabel = getDateTimeByFormat(new Date(mail?.created_at))?.date;
      if (dateLabel && dateLabel !== lastDateLabel) {
        result?.push({ type: "date", date: dateLabel });
        lastDateLabel = dateLabel;
      }
      result?.push({ type: "message", mail });
    });

    return result;
  }, [mails]);

  if (!selectedThreadId) {
    return (
      <div className="flex items-center justify-center h-full text-dark-gray dark:text-dark-gray-dk text-md">
        Select a thread to view
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col h-full overflow-hidden">
        <div className="shrink-0 h-[36px] flex items-center px-[12px] border-b border-border-gray dark:border-border-gray-dk">
          <div className="flex items-center gap-[6px]">
            <div className="w-[24px] h-[24px] shrink-0 rounded-full bg-gradient-to-r from-[var(--color-gray-75)] to-[var(--color-gray-100)] dark:from-[var(--color-gray-75-dk)] dark:to-[var(--color-gray-100-dk)] animate-pulse" />
            <div className="h-[14px] w-[160px] rounded bg-gradient-to-r from-[var(--color-gray-75)] to-[var(--color-gray-100)] dark:from-[var(--color-gray-75-dk)] dark:to-[var(--color-gray-100-dk)] animate-pulse" />
          </div>
        </div>
        <div className="shrink-0 h-[36px] flex items-center justify-between px-[12px] border-b border-border-gray dark:border-border-gray-dk">
          <div className="flex items-center gap-[6px]">
            <div className="h-[12px] w-[90px] rounded bg-gradient-to-r from-[var(--color-gray-75)] to-[var(--color-gray-100)] dark:from-[var(--color-gray-75-dk)] dark:to-[var(--color-gray-100-dk)] animate-pulse" />
            <div className="h-[12px] w-[80px] rounded bg-gradient-to-r from-[var(--color-gray-75)] to-[var(--color-gray-100)] dark:from-[var(--color-gray-75-dk)] dark:to-[var(--color-gray-100-dk)] animate-pulse" />
          </div>
          <div className="h-[12px] w-[140px] rounded bg-gradient-to-r from-[var(--color-gray-75)] to-[var(--color-gray-100)] dark:from-[var(--color-gray-75-dk)] dark:to-[var(--color-gray-100-dk)] animate-pulse" />
        </div>
        <div className="flex-1 flex flex-col overflow-hidden">
          {[...Array(4)].map((_, index) => (
            <EmailMessageShimmer key={`shimmer-${index}`} />
          ))}
        </div>
      </div>
    );
  }

  const subject = selectedThread?.subject || mails?.[0]?.subject || "";
  const contactEmail = selectedThread?.from_email || "";
  const contactInitial = contactEmail
    ? contactEmail?.charAt(0)?.toUpperCase()
    : "?";

  const defaultAssignedAgents = !selectedThread?.assigned_agents?.length
    ? []
    : selectedThread?.assigned_agents?.map((agentId, idx) => ({
        id: agentId,
        name: selectedThread?.assigned_agent_names?.[idx] || agentId,
        role: "",
        status: "active",
        profile_picture: null,
      })) || [];

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header - Contact email */}
      <div className="shrink-0 h-[36px] flex items-center px-[12px] border-b border-border-gray dark:border-border-gray-dk">
        <div className="flex items-center gap-[6px]">
          <Avatar size={24} text={contactInitial} className="shrink-0" />
          <span className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk truncate">
            {contactEmail}
          </span>
        </div>
      </div>

      {/* Subheader - Contact Owner + Subject */}
      <div className="shrink-0 h-[36px] flex items-center justify-between px-[12px] border-b border-border-gray dark:border-border-gray-dk">
        <div className="flex items-center gap-[6px]">
          <span className="text-md font-medium text-analytics-gray dark:text-analytics-gray-dk">
            Contact Owner
          </span>
          <AgentAssignmentDropdown
            selectedAgentIds={selectedThread?.assigned_agents ?? []}
            defaultSelectedAgents={defaultAssignedAgents}
            triggerText="Add Assignee"
            onApply={async (ids) => {
              await onAssignAgents?.(ids);
            }}
            triggerClassName="!border-none"
            triggerMaxWidth="120px"
          />
        </div>
        {subject?.trim() && (
          <Tooltip content={subject} contentClassName="text-md">
            <span className="text-md text-verba-indigo dark:text-verba-indigo-dk font-medium truncate max-w-[50%]">
              RE: {subject}
            </span>
          </Tooltip>
        )}
      </div>

      {/* Email Thread */}
      <div className="flex-1 overflow-y-auto min-h-0" ref={scrollRef}>
        {mailsWithDateSeparators?.map((item, idx) => {
          if (item?.type === "date") {
            return <DateStamp key={`date-${idx}`} date={item.date} />;
          }
          return <EmailMessageBlock key={item.mail.id} mail={item.mail} />;
        })}
      </div>

      {/* Bottom Bar - Email Compose */}
      {!isLoading && (
        <InboxInput
          onChooseTemplate={() => onEmailBuilderClick?.("my_templates")}
          onComposeEmail={() => onEmailBuilderClick?.("")}
        />
      )}
    </div>
  );
};

export default InboxConversationPanel;
